import { keyframes } from "@emotion/react";
import { APP_NAME } from "@xop-any-time/shared";
import { Typography } from "@mui/material";
import type { JSX } from "react";

/**
 * The page nameplate — "XOP Reseller." set large, bold and centred above the
 * hero, exactly as the customer support portal sets "Customer Support
 * Portal.": one line, `font-bold`, tight `-0.04em` tracking, and a
 * `clamp()`ed size so it scales with the viewport instead of stepping at
 * breakpoints. The words arrive one after another, 160ms apart.
 *
 * The stagger is what makes it read as a nameplate being *set* rather than a
 * heading fading in, so it is the part worth copying exactly. It is expressed
 * as an emotion keyframe rather than a global CSS class because this app
 * styles through MUI/emotion — `globals.css` is a verbatim copy of
 * xop-edge-staff's and adding a class to it would be drift.
 *
 * Colour is `text.primary`, not a fixed white: the reference's nameplate
 * flips with its theme toggle (white on the dark field, near-black on the
 * light one) and so must this one.
 *
 * The whole phrase is one heading for assistive tech; the per-word spans are
 * purely visual and the animation collapses to nothing under
 * `prefers-reduced-motion`.
 *
 * `text` defaults to the shared `APP_NAME` rather than re-typing it, so the
 * product's name still has exactly one spelling in the codebase. The
 * trailing full stop — "Customer Support Portal." is set with one — is
 * added by the nameplate treatment itself and stripped from `text` first if
 * already present, so a caller may pass the sentence either way. `id` is
 * forwarded to the `<h1>` so a section can `aria-labelledby` it.
 */
/** Per-word cadence (ms). Matches the reference's 160ms. */
const STEP_MS = 160;

const wordIn = keyframes`
  from { opacity: 0; transform: translateY(0.42em); filter: blur(6px); }
  to   { opacity: 1; transform: translateY(0);      filter: blur(0); }
`;

export function Nameplate({ text = APP_NAME, id }: { text?: string; id?: string } = {}): JSX.Element {
  // "XOP Networks" can repeat within a longer sentence, so the key needs
  // the index alongside the word.
  const words = text.trim().replace(/\.$/, "").split(/\s+/);
  return (
    <Typography
      variant="h1"
      component="h1"
      id={id}
      sx={{
        textAlign: "center",
        fontWeight: 700,
        lineHeight: 0.95,
        letterSpacing: "-0.04em",
        fontSize: "clamp(2rem, 4.4vw, 3.6rem)",
        color: "text.primary",
        mb: { xs: 5, lg: 7 },
      }}
    >
      {words.map((word, i) => (
        <Typography
          key={`${word}-${i}`}
          component="span"
          className="xop-nameplate-word"
          sx={{
            display: "inline-block",
            font: "inherit",
            letterSpacing: "inherit",
            whiteSpace: "pre",
            animation: `${wordIn} 900ms cubic-bezier(0.16,1,0.3,1) both`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
          style={{ animationDelay: `${i * STEP_MS}ms` }}
        >
          {i === words.length - 1 ? `${word}.` : `${word} `}
        </Typography>
      ))}
    </Typography>
  );
}
